import React, { useEffect, useMemo, useState } from "react";
import routePromo from "./routes";
import { useDispatch, useSelector } from "react-redux";
import { routeAuth } from "../AuthPage";
import { Redirect } from "react-router-dom";
import { deleteRequest, getRequest, postRequest, putRequest } from "../../services/requestController";
import { setLoading } from "../../redux/actions";

import "./styles.scss";

import {
    Alert,
    Button,
    Card,
    Col,
    Divider,
    Drawer,
    Empty,
    Image,
    Input,
    Modal,
    Progress,
    Row,
    Segmented,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    notification,
} from "antd";
import { BarChartOutlined, EyeOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { UserHeader } from "../../components/UserHeader/UserHeader";
import EditModal from "../../components/Modals/EditModal/EditModal";
import AddPromoModal from "../../components/Modals/AddPromoModal/AddPromoModal";
import axios from "../../axios-controller";

const { Title, Text } = Typography;

const ACTIVE_OPTIONS = [
    { label: "Включен", value: true },
    { label: "Выключен", value: false },
];

const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const safeStr = (v) => (typeof v === "string" ? v : v === null || v === undefined ? "" : String(v));

const toImageUrl = (path) => {
    if (!path) return "";
    try {
        return new URL(path, axios.defaults.baseURL || "/").toString();
    } catch {
        return String(path);
    }
};

const fileFromUploadValue = (v) => {
    const list = Array.isArray(v) ? v : [];
    const first = list[0];
    return first?.originFileObj || null;
};

const PromoPage = () => {
    const dispatch = useDispatch();
    const loading = useSelector((state) => state.loading);
    const token = useSelector((state) => state.token);

    const [promoData, setPromoData] = useState({});
    const [tableData, setTableData] = useState([]);
    const [rawPromos, setRawPromos] = useState([]);

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

    const [addOpen, setAddOpen] = useState(false);
    const [addLoading, setAddLoading] = useState(false);

    const [viewOpen, setViewOpen] = useState(false);
    const [viewPromo, setViewPromo] = useState(null);

    const [editOpen, setEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editPromo, setEditPromo] = useState(null);

    const [statusFilter, setStatusFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [insightsOpen, setInsightsOpen] = useState(false);

    const fetchPromo = async () => {
        const promoResponse = await getRequest("/api/admin/promo", token);

        if (!promoResponse?.success) {
            notification.error({
                message: promoResponse?.error?.message || "Ошибка",
                description: (promoResponse?.error?.description || []).join("\n"),
            });
            setPromoData({});
            setTableData([]);
            setRawPromos([]);
            return;
        }

        const payload = promoResponse.data || {};
        const rows = Array.isArray(payload.data) ? payload.data : [];

        setPromoData({
            count: payload.count || rows.length || 0,
            activePromo: payload.activePromo || 0,
            data: rows,
        });

        setRawPromos(rows);

        setTableData(
            rows.map((promo) => {
                const isActive = promo?.active === true;
                return {
                    key: promo.promoId,
                    promoId: promo.promoId,
                    image: promo.image,
                    title: promo.title,
                    description: promo.description,
                    active: isActive,
                    status: [isActive ? "активный" : "выключен"],
                    raw: promo,
                };
            })
        );

        setPagination((p) => ({ ...p, current: 1, pageSize: 10 }));
    };

    useEffect(() => {
        if (!token) return;

        const requestControl = async () => {
            try {
                dispatch(setLoading(true));
                await fetchPromo();
            } finally {
                dispatch(setLoading(false));
            }
        };

        requestControl();
    }, [token, dispatch]);

    const handleDeletePromo = async (promoId) => {
        const res = await deleteRequest(`/api/admin/promo/${promoId}`, token);

        if (!res?.success) {
            notification.error({
                message: res?.error?.message || "Ошибка",
                description: (res?.error?.description || []).join("\n"),
            });
            return;
        }

        notification.success({ message: "Успешно", description: res?.data?.message || "Акция удалена" });

        setTableData((prev) => {
            const next = prev.filter((p) => Number(p.promoId) !== Number(promoId));
            setPagination((pg) => {
                const maxPage = Math.max(1, Math.ceil(next.length / pg.pageSize));
                return pg.current > maxPage ? { ...pg, current: maxPage } : pg;
            });
            return next;
        });

        setPromoData((prev) => {
            const prevCount = safeNum(prev?.count || 0);
            const prevActivePromo = safeNum(prev?.activePromo || 0);
            const deletedWasActive = !!prev?.data?.find((x) => Number(x.promoId) === Number(promoId))?.active;

            const nextData = Array.isArray(prev?.data) ? prev.data.filter((x) => Number(x.promoId) !== Number(promoId)) : [];
            return {
                ...prev,
                count: Math.max(0, prevCount - 1),
                activePromo: Math.max(0, prevActivePromo - (deletedWasActive ? 1 : 0)),
                data: nextData,
            };
        });

        setRawPromos((prev) => (Array.isArray(prev) ? prev.filter((x) => Number(x?.promoId) !== Number(promoId)) : prev));
    };

    const createPromo = async ({ title, description, active, imageFile }) => {
        if (!title) {
            notification.error({ message: "Ошибка", description: "Название обязательно" });
            return;
        }

        const formData = new FormData();
        formData.append("title", title);
        formData.append("description", description || '');
        formData.append("active", String(!!active));
        if (imageFile) formData.append("image", imageFile);

        setAddLoading(true);
        try {
            const res = await postRequest("/api/admin/promo/create", formData, token);

            if (!res?.success) {
                notification.error({
                    message: res?.error?.message || "Ошибка",
                    description: (res?.error?.description || []).join("\n"),
                });
                return;
            }

            notification.success({ message: "Успешно", description: "Промоакция создана" });
            setAddOpen(false);
            await fetchPromo();
        } finally {
            setAddLoading(false);
        }
    };

    const editPromoSubmit = async (values) => {
        const promoId = Number(editPromo?.promoId);
        if (!promoId || Number.isNaN(promoId)) {
            notification.error({ message: "Ошибка", description: "Некорректный promoId" });
            return;
        }

        const title = typeof values?.title === "string" ? values.title.trim() : "";
        const description = typeof values?.description === "string" ? values.description.trim() : "";
        const active = values?.active === true;
        const imageFile = fileFromUploadValue(values?.image);

        if (!title) {
            notification.error({ message: "Ошибка", description: "Название обязательно" });
            return;
        }

        const formData = new FormData();
        formData.append("title", title);
        formData.append("description", description);
        formData.append("active", String(active));
        if (imageFile) formData.append("image", imageFile);

        setEditLoading(true);
        try {
            const res = await putRequest(`/api/admin/promo/${promoId}`, formData, token);

            if (!res?.success) {
                notification.error({
                    message: res?.error?.message || "Ошибка",
                    description: (res?.error?.description || []).join("\n"),
                });
                return;
            }

            notification.success({ message: "Успешно", description: "Промоакция обновлена" });
            setEditOpen(false);
            setEditPromo(null);
            await fetchPromo();
        } finally {
            setEditLoading(false);
        }
    };

    const normalized = useMemo(() => {
        const list = Array.isArray(rawPromos) ? rawPromos : [];
        return list
            .map((p) => ({
                promoId: safeNum(p?.promoId),
                title: safeStr(p?.title).trim(),
                description: safeStr(p?.description).trim(),
                image: p?.image,
                active: p?.active === true,
                raw: p,
            }))
            .filter((p) => p.promoId > 0);
    }, [rawPromos]);

    const filteredTableData = useMemo(() => {
        const q = safeStr(query).trim().toLowerCase();

        return (Array.isArray(tableData) ? tableData : []).filter((row) => {
            if (statusFilter === "active" && row.active !== true) return false;
            if (statusFilter === "inactive" && row.active === true) return false;

            if (!q) return true;
            const t = safeStr(row.title).toLowerCase();
            const d = safeStr(row.description).toLowerCase();
            const id = safeStr(row.promoId).toLowerCase();
            return t.includes(q) || d.includes(q) || id.includes(q);
        });
    }, [tableData, statusFilter, query]);

    const insights = useMemo(() => {
        const total = normalized.length;
        const active = normalized.filter((p) => p.active).length;
        const inactive = total - active;

        const activePct = total ? Math.round((active / total) * 100) : 0;
        const inactivePct = total ? 100 - activePct : 0;

        const missingImages = normalized.filter((p) => !p.image).length;
        const missingImagesPct = total ? Math.round((missingImages / total) * 100) : 0;

        const avgTitleLen = total ? Math.round(normalized.reduce((s, p) => s + p.title.length, 0) / total) : 0;
        const avgDescLen = total ? Math.round(normalized.reduce((s, p) => s + p.description.length, 0) / total) : 0;

        const longest = [...normalized].sort((a, b) => b.description.length - a.description.length).slice(0, 5);
        const newest = [...normalized].sort((a, b) => b.promoId - a.promoId)[0] || null;

        const keywords = (() => {
            const stop = new Set([
                "и",
                "в",
                "во",
                "на",
                "по",
                "к",
                "с",
                "со",
                "за",
                "для",
                "от",
                "до",
                "или",
                "а",
                "но",
                "что",
                "это",
                "как",
                "мы",
                "вы",
                "они",
                "он",
                "она",
                "оно",
                "так",
                "же",
                "не",
                "нет",
                "да",
                "все",
                "всё",
                "у",
                "из",
                "при",
                "без",
                "под",
                "над",
                "про",
                "если",
                "то",
                "ещё",
                "еще",
            ]);

            const map = new Map();
            normalized.forEach((p) => {
                const text = `${p.title} ${p.description}`.toLowerCase();
                text
                    .replace(/[^a-zа-я0-9\s-]/gi, " ")
                    .split(/\s+/)
                    .map((w) => w.trim())
                    .filter((w) => w.length >= 4 && !stop.has(w))
                    .forEach((w) => map.set(w, (map.get(w) || 0) + 1));
            });

            return [...map.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
                .map(([word, count]) => ({ word, count }));
        })();

        const qualityScore = (() => {
            const base = 100;
            const imgPenalty = Math.min(60, Math.round(missingImagesPct * 0.6));
            const activePenalty = activePct < 10 ? 20 : activePct < 25 ? 10 : 0;
            const textPenalty = avgDescLen < 40 ? 15 : avgDescLen < 80 ? 8 : 0;
            return Math.max(0, base - Math.max(imgPenalty, activePenalty, textPenalty));
        })();

        return {
            total,
            active,
            inactive,
            activePct,
            inactivePct,
            missingImages,
            missingImagesPct,
            avgTitleLen,
            avgDescLen,
            longest,
            newest,
            keywords,
            qualityScore,
        };
    }, [normalized]);

    const handlePositionChange = async (promoId, direction) => {
        try {
            const res = await putRequest(`/api/admin/promo/${promoId}/position`, { direction }, token);

            if (!res?.success) {
                notification.error({
                    message: res?.error?.message || "Ошибка",
                    description: (res?.error?.description || []).join("\n"),
                });
                return;
            }

            notification.success({ 
                message: "Успешно", 
                description: `Позиция изменена` 
            });

            await fetchPromo();
        } catch (error) {
            notification.error({
                message: "Ошибка",
                description: "Не удалось изменить позицию",
            });
        }
    };

    const columns = useMemo(
        () => [
            {
                title: "Баннер",
                dataIndex: "image",
                key: "image",
                width: 220,
                render: (image, record) => (
                    <Space size={10}>
                        <Image
                            src={toImageUrl(image)}
                            alt="promo banner"
                            style={{ width: 92, height: 62, objectFit: "cover", borderRadius: 10 }}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4nGNgYGBgAAAABQABh6FO1AAAAABJRU5ErkJggg=="
                        />
                        <Space direction="vertical" size={2}>
                            <Text strong>#{safeStr(record?.promoId) || "—"}</Text>
                            <Text type="secondary" style={{ maxWidth: 120 }} ellipsis>
                                {safeStr(record?.title) || "—"}
                            </Text>
                        </Space>
                    </Space>
                ),
            },
            {
                title: "Позиция",
                dataIndex: "position",
                key: "position",
                width: 100,
                sorter: (a, b) => (a.raw?.position || 0) - (b.raw?.position || 0),
                render: (_, record) => (
                    <Tag color="blue">#{record.raw?.position || 0}</Tag>
                ),
            },
            {
                title: "Заголовок",
                dataIndex: "title",
                key: "title",
                width: 200,
                ellipsis: true,
                render: (v) => {
                    const s = safeStr(v).trim();
                    return s ? <Text>{s}</Text> : <Text type="secondary">—</Text>;
                },
            },
            {
                title: "Описание",
                dataIndex: "description",
                key: "description",
                width: 100,
                ellipsis: true,
                render: (v) => {
                    const s = safeStr(v).trim();
                    if (!s) return <Text type="secondary">—</Text>;
                    return (
                        <Tooltip title={<Text style={{ color: "#fff", whiteSpace: "pre-wrap" }}>{s}</Text>}>
                            <Text ellipsis>{s}</Text>
                        </Tooltip>
                    );
                },
            },
            {
                title: "Статус",
                key: "status",
                dataIndex: "status",
                width: 140,
                filters: [
                    { text: "Активный", value: "active" },
                    { text: "Выключен", value: "inactive" },
                ],
                onFilter: (value, record) => (value === "active" ? record.active === true : record.active !== true),
                render: (_, { status }) => (
                    <>
                        {status.map((stat) => {
                            const color = stat === "активный" ? "green" : "volcano";
                            return (
                                <Tag color={color} key={stat}>
                                    {String(stat).toUpperCase()}
                                </Tag>
                            );
                        })}
                    </>
                ),
            },
            {
                title: "Действие",
                key: "action",
                width: 300,
                render: (_, record) => (
                    <Space size="middle" wrap>
                        <Tooltip title="Переместить вверх">
                            <Button 
                                type="text" 
                                size="small"
                                icon={<span>↑</span>}
                                onClick={() => handlePositionChange(record.promoId, 'up')}
                            />
                        </Tooltip>
                        <Tooltip title="Переместить вниз">
                            <Button 
                                type="text" 
                                size="small"
                                icon={<span>↓</span>}
                                onClick={() => handlePositionChange(record.promoId, 'down')}
                            />
                        </Tooltip>
                        <a
                            onClick={() => {
                                setViewPromo(record.raw);
                                setViewOpen(true);
                            }}
                        >
                            Просмотр
                        </a>
                        <a
                            onClick={() => {
                                setEditPromo(record.raw);
                                setEditOpen(true);
                            }}
                        >
                            Редактировать
                        </a>
                        <a
                            className={"red"}
                            onClick={() => {
                                Modal.confirm({
                                    title: "Удалить промоакцию?",
                                    okText: "Удалить",
                                    cancelText: "Отмена",
                                    okButtonProps: { danger: true },
                                    onOk: () => handleDeletePromo(record.promoId),
                                });
                            }}
                        >
                            Удалить
                        </a>
                    </Space>
                ),
            },
        ],
        []
    );

    if (!token) return <Redirect to={routeAuth()} />;

    const editInitialValues = editPromo
        ? { image: [], title: editPromo.title ?? "", description: editPromo.description ?? "", active: editPromo.active === true }
        : { image: [], title: "", description: "", active: false };

    const qualityColor = insights.qualityScore >= 75 ? "#52c41a" : insights.qualityScore >= 50 ? "#faad14" : "#ff4d4f";

    return (
        <>
            {!loading && (
                <>
                    <div className="container promo-page">
                        <UserHeader />

                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} md={14}>
                                <Space direction="vertical" size={4}>
                                    <Title level={3} style={{ marginBottom: 0 }}>
                                        Промоакции
                                    </Title>
                                    <Text type="secondary">Сводка, быстрые фильтры, качество контента и управление списком.</Text>
                                </Space>
                            </Col>
                            <Col xs={24} md={10} style={{ textAlign: "right" }}>
                                <Space wrap>
                                    <Button icon={<ReloadOutlined />} onClick={fetchPromo}>
                                        Обновить
                                    </Button>
                                    <Button icon={<BarChartOutlined />} onClick={() => setInsightsOpen(true)}>
                                        Инсайты
                                    </Button>
                                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
                                        Добавить промоакцию
                                    </Button>
                                </Space>
                            </Col>
                        </Row>

                        <Divider />

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={6}>
                                <Card bordered={false} className="promo-card promo-card--accent">
                                    <Statistic title="Всего промо" value={insights.total} />
                                    <Text type="secondary">Записей в системе</Text>
                                </Card>
                            </Col>
                            <Col xs={24} md={6}>
                                <Card bordered={false} className="promo-card">
                                    <Statistic title="Активные" value={insights.active} />
                                    <Progress percent={insights.activePct} showInfo={false} strokeColor="#52c41a" style={{ marginTop: 10 }} />
                                </Card>
                            </Col>
                            <Col xs={24} md={6}>
                                <Card bordered={false} className="promo-card">
                                    <Statistic title="Выключенные" value={insights.inactive} />
                                    <Progress percent={insights.inactivePct} showInfo={false} strokeColor="#fa541c" style={{ marginTop: 10 }} />
                                </Card>
                            </Col>
                            <Col xs={24} md={6}>
                                <Card bordered={false} className="promo-card">
                                    <Statistic title="Качество витрины" value={insights.qualityScore} suffix="/100" />
                                    <Progress percent={insights.qualityScore} showInfo={false} strokeColor={qualityColor} style={{ marginTop: 10 }} />
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                            <Col xs={24} md={16}>
                                <Card
                                    bordered={false}
                                    className="promo-card"
                                    title="Фильтры и поиск"
                                    extra={
                                        <Space>
                                            <Segmented
                                                value={statusFilter}
                                                onChange={(v) => {
                                                    setStatusFilter(v);
                                                    setPagination((p) => ({ ...p, current: 1 }));
                                                }}
                                                options={[
                                                    { label: "Все", value: "all" },
                                                    { label: "Активные", value: "active" },
                                                    { label: "Выключенные", value: "inactive" },
                                                ]}
                                            />
                                            <Button
                                                onClick={() => {
                                                    setStatusFilter("all");
                                                    setQuery("");
                                                    setPagination((p) => ({ ...p, current: 1 }));
                                                }}
                                            >
                                                Сбросить
                                            </Button>
                                        </Space>
                                    }
                                >
                                    <Row gutter={[12, 12]} align="middle">
                                        <Col xs={24} md={14}>
                                            <Input
                                                allowClear
                                                value={query}
                                                onChange={(e) => {
                                                    setQuery(e.target.value);
                                                    setPagination((p) => ({ ...p, current: 1 }));
                                                }}
                                                prefix={<SearchOutlined />}
                                                placeholder="Поиск по ID, заголовку или описанию"
                                            />
                                        </Col>
                                        <Col xs={24} md={10}>
                                            <Alert
                                                type="info"
                                                showIcon
                                                message="Подсказка"
                                                description={
                                                    insights.missingImages
                                                        ? `Есть промо без баннера: ${insights.missingImages} (${insights.missingImagesPct}%).`
                                                        : "Все промо добавлены с баннерами"
                                                }
                                            />
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>

                            <Col xs={24} md={8}>
                                <Card bordered={false} className="promo-card" title="Пульс контента" extra={<EyeOutlined />}>
                                    {insights.total ? (
                                        <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                            <Row gutter={[12, 12]}>
                                                <Col span={12}>
                                                    <Card bordered={false} className="promo-mini">
                                                        <Statistic title="Средн. заголовок" value={insights.avgTitleLen} suffix="симв." />
                                                    </Card>
                                                </Col>
                                                <Col span={12}>
                                                    <Card bordered={false} className="promo-mini">
                                                        <Statistic title="Средн. описание" value={insights.avgDescLen} suffix="симв." />
                                                    </Card>
                                                </Col>
                                            </Row>

                                            <Card bordered={false} className="promo-mini promo-mini--soft">
                                                <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                                    <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                                        <Text type="secondary">Баннеры заполнены</Text>
                                                        <Text strong>{Math.max(0, 100 - insights.missingImagesPct)}%</Text>
                                                    </Space>
                                                    <Progress
                                                        percent={Math.max(0, 100 - insights.missingImagesPct)}
                                                        showInfo={false}
                                                        strokeColor={insights.missingImagesPct ? "#faad14" : "#52c41a"}
                                                    />
                                                </Space>
                                            </Card>

                                            {insights.newest ? (
                                                <Card bordered={false} className="promo-mini">
                                                    <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                                        <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                                            <Text type="secondary">Самая новая</Text>
                                                            <Tag color={insights.newest.active ? "green" : "volcano"}>
                                                                #{insights.newest.promoId} {insights.newest.active ? "ACTIVE" : "OFF"}
                                                            </Tag>
                                                        </Space>
                                                        <Text strong ellipsis>
                                                            {insights.newest.title || "—"}
                                                        </Text>
                                                        <Text type="secondary" ellipsis>
                                                            {insights.newest.description || "—"}
                                                        </Text>
                                                    </Space>
                                                </Card>
                                            ) : null}
                                        </Space>
                                    ) : (
                                        <Empty description="Промо пока нет" />
                                    )}
                                </Card>
                            </Col>
                        </Row>

                        <Divider />

                        <Card
                            bordered={false}
                            className="promo-card"
                            title="Таблица промоакций"
                            extra={
                                <Space wrap>
                                    <Tag color="blue">Показано: {filteredTableData.length}</Tag>
                                    <Tag>Всего: {insights.total}</Tag>
                                </Space>
                            }
                        >
                            <Table
                                columns={columns}
                                dataSource={filteredTableData}
                                rowKey="promoId"
                                expandable={{
                                    expandedRowRender: (record) => {
                                        const raw = record?.raw || {};
                                        const title = safeStr(raw?.title).trim();
                                        const description = safeStr(raw?.description).trim();
                                        return (
                                            <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 16, alignItems: "start" }}>
                                                <div>
                                                    <Image
                                                        src={toImageUrl(raw?.image)}
                                                        alt="promo banner"
                                                        style={{ width: "100%", maxWidth: 240, height: 140, objectFit: "cover", borderRadius: 12 }}
                                                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4nGNgYGBgAAAABQABh6FO1AAAAABJRU5ErkJggg=="
                                                    />
                                                </div>
                                                <div>
                                                    <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                                        <Space size={10} wrap>
                                                            <Text strong>#{safeStr(raw?.promoId) || "—"}</Text>
                                                            <Tag color={raw?.active ? "green" : "volcano"}>
                                                                {(raw?.active ? "активный" : "выключен").toUpperCase()}
                                                            </Tag>
                                                        </Space>
                                                        <Text strong>{title || "—"}</Text>
                                                        <Text style={{ whiteSpace: "pre-wrap" }}>{description || "—"}</Text>
                                                    </Space>
                                                </div>
                                            </div>
                                        );
                                    },
                                    rowExpandable: (record) => !!record?.raw,
                                }}
                                onChange={(nextPagination) => setPagination({ current: nextPagination.current, pageSize: 10 })}
                                pagination={{
                                    current: pagination.current,
                                    pageSize: 10,
                                    total: filteredTableData.length,
                                    showSizeChanger: false,
                                    showTotal: (total) => `Всего: ${total}`,
                                }}
                            />
                        </Card>
                    </div>

                    <AddPromoModal
                        open={addOpen}
                        onClose={() => setAddOpen(false)}
                        onSubmit={createPromo}
                        confirmLoading={addLoading}
                        initialValues={{ active: false, image: [] }}
                    />

                    <EditModal
                        open={editOpen}
                        title={editPromo ? `Редактирование промоакции #${editPromo.promoId}` : "Редактирование промоакции"}
                        onClose={() => {
                            setEditOpen(false);
                            setEditPromo(null);
                        }}
                        onSubmit={editPromoSubmit}
                        confirmLoading={editLoading}
                        initialValues={editInitialValues}
                        okText="Сохранить"
                        cancelText="Отмена"
                        fields={[
                            { type: "upload", name: "image", label: "Картинка", placeholder: "Выбрать изображение", props: { accept: "image/*", maxCount: 1 } },
                            { type: "input", name: "title", label: "Заголовок", rules: [{ required: true, message: "Введите заголовок" }], props: { style: { width: "100%" } } },
                            { type: "input", name: "description", label: "Описание (необязательно)", props: { style: { width: "100%" } } },
                            { type: "select", name: "active", label: "Статус", options: ACTIVE_OPTIONS, rules: [{ required: true, message: "Выберите статус" }], props: { style: { width: "100%" } } },
                        ]}
                    />

                    <Modal
                        open={viewOpen}
                        title={viewPromo ? `Промоакция #${viewPromo.promoId}` : "Промоакция"}
                        onCancel={() => {
                            setViewOpen(false);
                            setViewPromo(null);
                        }}
                        footer={[
                            <Button
                                key="close"
                                onClick={() => {
                                    setViewOpen(false);
                                    setViewPromo(null);
                                }}
                            >
                                Закрыть
                            </Button>,
                        ]}
                        width={820}
                    >
                        {viewPromo ? (
                            <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16 }}>
                                <div>
                                    <Image
                                        src={toImageUrl(viewPromo.image)}
                                        alt="promo banner"
                                        style={{ width: "100%", maxWidth: 260, height: 160, objectFit: "cover", borderRadius: 12 }}
                                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4nGNgYGBgAAAABQABh6FO1AAAAABJRU5ErkJggg=="
                                    />
                                </div>
                                <div>
                                    <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                        <Space size={10} wrap>
                                            <Text strong>#{safeStr(viewPromo.promoId) || "—"}</Text>
                                            <Tag color={viewPromo.active ? "green" : "volcano"}>
                                                {(viewPromo.active ? "активный" : "выключен").toUpperCase()}
                                            </Tag>
                                        </Space>
                                        <div>
                                            <Text type="secondary">Заголовок</Text>
                                            <div>
                                                <Text strong>{safeStr(viewPromo.title) || "—"}</Text>
                                            </div>
                                        </div>
                                        <div>
                                            <Text type="secondary">Описание</Text>
                                            <div>
                                                <Text style={{ whiteSpace: "pre-wrap" }}>{safeStr(viewPromo.description) || "—"}</Text>
                                            </div>
                                        </div>
                                    </Space>
                                </div>
                            </div>
                        ) : null}
                    </Modal>

                    <Drawer
                        open={insightsOpen}
                        onClose={() => setInsightsOpen(false)}
                        title="Инсайты по промо"
                        width={720}
                        destroyOnClose
                        extra={
                            <Button onClick={() => setInsightsOpen(false)}>
                                Закрыть
                            </Button>
                        }
                    >
                        <Space direction="vertical" size={14} style={{ width: "100%" }}>
                            <Row gutter={[12, 12]}>
                                <Col span={12}>
                                    <Card bordered={false} className="promo-mini">
                                        <Statistic title="Всего" value={insights.total} />
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card bordered={false} className="promo-mini">
                                        <Statistic title="Активные" value={insights.active} />
                                    </Card>
                                </Col>
                            </Row>

                            <Card bordered={false} className="promo-card" title="Состав по статусам">
                                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                    <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                        <Text type="secondary">Активные</Text>
                                        <Text strong>
                                            {insights.active} ({insights.activePct}%)
                                        </Text>
                                    </Space>
                                    <Progress percent={insights.activePct} showInfo={false} strokeColor="#52c41a" />
                                    <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                        <Text type="secondary">Выключенные</Text>
                                        <Text strong>
                                            {insights.inactive} ({insights.inactivePct}%)
                                        </Text>
                                    </Space>
                                    <Progress percent={insights.inactivePct} showInfo={false} strokeColor="#fa541c" />
                                </Space>
                            </Card>

                            <Card bordered={false} className="promo-card" title="Топ-5 самых “длинных” описаний">
                                {insights.longest.length ? (
                                    <Table
                                        size="small"
                                        pagination={false}
                                        rowKey={(r) => String(r.promoId)}
                                        dataSource={insights.longest}
                                        columns={[
                                            {
                                                title: "ID",
                                                dataIndex: "promoId",
                                                key: "promoId",
                                                width: 90,
                                                render: (v) => <Text strong>#{v}</Text>,
                                            },
                                            {
                                                title: "Заголовок",
                                                dataIndex: "title",
                                                key: "title",
                                                ellipsis: true,
                                                render: (v, r) => (
                                                    <Space size={8}>
                                                        <Tag color={r.active ? "green" : "volcano"}>{r.active ? "ACTIVE" : "OFF"}</Tag>
                                                        <Text ellipsis>{safeStr(v) || "—"}</Text>
                                                    </Space>
                                                ),
                                            },
                                            {
                                                title: "Длина",
                                                key: "len",
                                                width: 90,
                                                render: (_, r) => <Text type="secondary">{safeNum(r.description.length)}</Text>,
                                            },
                                        ]}
                                    />
                                ) : (
                                    <Empty description="Нет данных" />
                                )}
                            </Card>

                            <Card bordered={false} className="promo-card" title="Топ ключевых слов">
                                {insights.keywords.length ? (
                                    <Space wrap size={[8, 8]}>
                                        {insights.keywords.map((k) => (
                                            <Tag key={k.word} color="blue">
                                                {k.word} · {k.count}
                                            </Tag>
                                        ))}
                                    </Space>
                                ) : (
                                    <Empty description="Недостаточно текста для анализа" />
                                )}
                            </Card>

                            <Alert
                                showIcon
                                type={insights.qualityScore >= 75 ? "success" : insights.qualityScore >= 50 ? "warning" : "error"}
                                message={insights.qualityScore >= 75 ? "Витрина выглядит сильной" : insights.qualityScore >= 50 ? "Можно улучшить" : "Нужно подтянуть контент"}
                                description={
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">
                                            Промо без баннера: <Text strong>{insights.missingImages}</Text>
                                        </Text>
                                        <Text type="secondary">
                                            Средняя длина описания: <Text strong>{insights.avgDescLen}</Text>
                                        </Text>
                                    </Space>
                                }
                            />
                        </Space>
                    </Drawer>
                </>
            )}
        </>
    );
};

export { routePromo };

export default PromoPage;